import { DashboardModule, Form, Grid } from '@domain-group/suit-theme'

import handleCallbackAction from '@functions/handleCallbackAction'
import { getIntegrationFields } from '@functions/integrationPlatform'

import useIntegration, { useIntegrationForm } from '@hooks/useIntegration'
import useIntegrations from '@hooks/useIntegrations'

import useAgency, { useAgencyForm } from '@hooks/useAgency'

import type { IntegrationModel } from '@models/types'

interface CRMSyncProps {
  agencyPayload: ReturnType<typeof useAgency>,
  disabled: boolean,
}

const CRMSync = (props: CRMSyncProps) => {
  const { agencyPayload, disabled } = props
  const {
    agency,
    callbacks: {
      updateAgency: updateFn,
    },
  } = agencyPayload

  const agencyFormOptions = {
    // @ts-expect-error TS7006
    onBlurFn: formState => handleCallbackAction({
      callbacks: { action: updateFn },
      entityParams: formState,
      toastText: 'CRM Updated',
    }),
  }

  const agencyFormPayload = useAgencyForm(
    agency,
    agencyFormOptions,
  )

  const {
    entityState: {
      id: agencyId,
      options: {
        crm_sync_api: serviceProviderKey,
      },
    },
    handlers: agencyFormHandlers,
  } = agencyFormPayload

  // Initial integration state and also the filters used to find the integration
  const initIntegrationState: Partial<IntegrationModel> = {
    integrable_id: agencyId,
    integrable_type: 'Agency',
    service_provider: serviceProviderKey,
  }

  const integrationsPayload = useIntegrations({
    filters: initIntegrationState,
    performHttpRequests: !!agencyId,
  })

  const {
    filteredIntegrations,
    hasFilteredIntegrations,
    loading,
  } = integrationsPayload

  // If no integration then the initial integration state will be used
  const initIntegration = hasFilteredIntegrations ? filteredIntegrations[0] : initIntegrationState

  const integrationPayload = useIntegration(initIntegration)

  const {
    callbacks: {
      createOrUpdateIntegration: createOrUpdateFn,
    },
  } = integrationPayload

  const integrationFormOptions = {
    customDeps: [serviceProviderKey],
    // @ts-expect-error TS7006
    onBlurFn: formState => handleCallbackAction({
      // @ts-expect-error TS2322
      callbacks: { action: createOrUpdateFn },
      entityParams: formState,
      toastText: 'CRM Sync Updated',
    }),
  }

  const integrationFormPayload = useIntegrationForm(
    initIntegration,
    integrationFormOptions,
  )

  const {
    entityState: {
      access_token,
      data: {
        client_id, office_id, password, username,
      },
    },
    handlers,
  } = integrationFormPayload

  const supportedFields = getIntegrationFields(serviceProviderKey)

  return (
    <DashboardModule boxProps={{ marginBottom: 'large' }} loading={loading} title="CRM Sync">
      <Grid
        // @ts-expect-error TS2322
        gridGap="medium"
        gridTemplateColumns={[
          'repeat(1, minmax(160px, 1fr))',
          'repeat(1, minmax(160px, 1fr))',
          'repeat(1, minmax(160px, 1fr))',
          'repeat(2, minmax(160px, 1fr))',
        ]}
        padding="large"
      >
        <Form.Field label="Sync API">
          {/*
           // @ts-expect-error TS2322 */}
          <select disabled={disabled} value={serviceProviderKey} name="options.crm_sync_api" {...agencyFormHandlers}>
            <option value="">Not Enabled</option>
            <option value="agentbox">Agentbox</option>
            <option value="campaigntrack">Campaigntrack</option>
            <option value="mydesktop">MyDesktop</option>
            <option value="rex">Rex</option>
            <option value="vaultre">VaultRE</option>
          </select>
        </Form.Field>

        {supportedFields.includes('access_token') && (
          <Form.Field label="API Key">
            <input disabled={disabled} type="text" value={access_token} name="access_token" {...handlers} />
          </Form.Field>
        )}

        {supportedFields.includes('client_id') && (
          <Form.Field label="Client ID">
            <input disabled={disabled} type="text" value={client_id} name="data.client_id" {...handlers} />
          </Form.Field>
        )}

        {supportedFields.includes('office_id') && (
          <Form.Field label="Office ID">
            <input disabled={disabled} type="text" value={office_id} name="data.office_id" {...handlers} />
          </Form.Field>
        )}

        {supportedFields.includes('username') && (
          <Form.Field label="Username">
            <input disabled={disabled} type="text" value={username} name="data.username" {...handlers} />
          </Form.Field>
        )}

        {supportedFields.includes('password') && (
          <Form.Field label="Password">
            <input disabled={disabled} type="text" value={password} name="data.password" {...handlers} />
          </Form.Field>
        )}
      </Grid>
    </DashboardModule>
  )
}

export default CRMSync
